import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { CardsList } from '@/components/Profile/Cards/CardsList';
import { CardsEmpty } from '@/components/Profile/Cards/CardsEmpty';
import { TechIssues } from '@/components/Profile/Cards/TechIssues';
import { Spinner } from '@/components/Spinner/Spinner';
import { ErrorIcon, SuccessFilledIcon } from '@/components/UI/Icons/Icons';
import { useToastStore } from '@/store/useToastStore';
import { useUserCards } from './hooks';
import type { CardNotifyVariant } from './hooks';
import * as S from './PaymentMethodsSection.styles';

const NOTIFY_ICON = {
  success: <SuccessFilledIcon width={20} height={20} />,
  error: <ErrorIcon width={20} height={20} />,
} as const satisfies Record<'success' | 'error', React.ReactNode>;

export const PaymentMethodsSection = () => {
  const { t } = useTranslation();
  const { openToast } = useToastStore();

  const handleNotify = useCallback(
    (message: string, variant: CardNotifyVariant) => {
      if (variant === 'addFailed') {
        openToast({
          text: message,
          size: 'compact',
          borderRadius: 14,
        });
        return;
      }

      openToast({
        text: message,
        icon: NOTIFY_ICON[variant],
        size: 'compact',
        borderRadius: 14,
      });
    },
    [openToast]
  );

  const {
    cards,
    isLoading,
    isError,
    addCard,
    updateCard,
    deleteCard,
    setPrimaryCard,
  } = useUserCards({ onNotify: handleNotify });

  const handleRenameCard = useCallback(
    (id: number, name: string, onSuccess?: () => void) => {
      updateCard(id, { name }, onSuccess);
    },
    [updateCard]
  );

  const isInitialLoading = isLoading && cards.length === 0;
  const canShowAgreement = !isError && !isInitialLoading;

  function renderContent() {
    if (isLoading && !cards.length) {
      return (
        <S.LoadingBox>
          <Spinner size="m" />
        </S.LoadingBox>
      );
    }

    if (isError) {
      return <TechIssues />;
    }

    if (cards.length) {
      return (
        <CardsList
          cards={cards}
          onAddCard={addCard}
          onRenameCard={handleRenameCard}
          onDeleteCard={deleteCard}
          onMakePrimary={setPrimaryCard}
        />
      );
    }

    return <CardsEmpty onAddCard={addCard} />;
  }

  return (
    <S.Box>
      {renderContent()}
      {canShowAgreement && (
        <S.AgreementText buttonName={t('pages.cabinet.add_new_card')} />
      )}
    </S.Box>
  );
};
